import { z } from 'zod';

import { RawPhoneNumberSchema } from '~/schemas/PhoneNumberSchema';
import {
  AddressSchema,
  EmailSchema,
  FirstNameFormSchema,
  LastNameFormSchema,
} from '~/schemas/user';
import { dateOnly } from '~/utils/datetime';
import { supplierNpiRegex } from '~/utils/validationRegex';

export const ADDITIONAL_INFO_MAX_LENGTH = 500;
export const AdditionalInformationSchema = z
  .string()
  .max(ADDITIONAL_INFO_MAX_LENGTH, {
    error: 'Must be 500 characters or less',
  })
  .optional();

export const ContactSchema = z.object({
  firstName: FirstNameFormSchema,
  lastName: LastNameFormSchema,
  phoneNumber: RawPhoneNumberSchema,
  phoneType: z
    .string({
      error: (issue) =>
        issue.input === undefined ? 'Phone type is required' : 'Phone type is invalid',
    })
    .nonempty({ error: 'Phone type is required' }),
  email: EmailSchema.nullish().or(z.literal('')),
  preferredLanguage: z
    .string({
      error: (issue) =>
        issue.input === undefined
          ? 'Preferred language is required'
          : 'Preferred language is invalid',
    })
    .nonempty({ error: 'Preferred language is required' }),
  relationship: z
    .string({
      error: (issue) =>
        issue.input === undefined ? 'Relationship is required' : 'Relationship is invalid',
    })
    .nonempty({ error: 'Relationship is required' }),
});

export type OrderContact = z.infer<typeof ContactSchema>;

export const emptyContact = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  phoneType: 'MOBILE',
  email: '',
  preferredLanguage: 'ENGLISH',
  relationship: 'SELF',
};

const PhysicianFirstNameSchema = z
  .string({
    error: (issue) =>
      issue.input === undefined ? 'First Name is Required' : 'First Name is Invalid',
  })
  .min(1, { error: 'First Name is too short' })
  .max(50, { error: 'First Name is too long' })
  .transform((val) => val.trim());

export const PhysicianSchema = z.object(
  {
    address: AddressSchema,
    email: EmailSchema,
    firstName: PhysicianFirstNameSchema,
    lastName: LastNameFormSchema,
    npi: z
      .string({
        error: (issue) =>
          issue.input === undefined ? 'Supplier NPI is Required' : 'Supplier NPI is Invalid',
      })
      .min(10, { error: 'Supplier NPI is Invalid' })
      .refine((value) => supplierNpiRegex.test(value), {
        error: 'Supplier NPI is Invalid',
      }),
    patientLastVisit: z
      .string()
      .nullish()
      .superRefine((val, ctx) => {
        if (!val) return;
        const date = new Date(val);
        if (isNaN(date.getTime())) {
          ctx.addIssue({
            code: 'custom',
            message: 'Must be a valid date',
          });
        }
      })
      .transform((val) => (val ? dateOnly(val) : '')),
    phone: RawPhoneNumberSchema,
  },
  { error: 'Must provide a signing clinician' }
);
